import rsocket from '@/api/rsocket'
import Vue from 'vue'
import _ from 'lodash'
import store from '@/store'

import {
  encodeAndAddCustomMetadata,
  encodeAndAddWellKnownMetadata,
  MAX_STREAM_ID,
  MESSAGE_RSOCKET_ROUTING
} from 'rsocket-core'

function encodeRoute(route) {
  const length = Buffer.byteLength(route, 'utf8')
  const buffer = Buffer.alloc(1)
  buffer.writeInt8(length)
  return Buffer.concat([buffer, Buffer.from(route, 'utf8')])
}

function encodeBearer(token) {
  const headerBuffer = new Buffer([129])
  const tokenBuffer = Buffer.from(token, 'utf8')
  const buffer = Buffer.concat([headerBuffer, tokenBuffer])
  return new Uint8Array(buffer.buffer)
}

function eventId(event) {
  return event.eventType + '_' + event.flight.id
}

const state = {
  subscription: null,
  isFeedAlive: false,
  isSocketAlive: false,
  items: {},
  filter: {
    starredEvents: [],
    stands: []
  },
  request: { apIata: 'RIX' }
}

const getters = {
  allItems: (state) => _.sortBy(state.items, 'time'),
  depItems: (state, getters) =>
    Object.values(getters.allItems.filter((item) => item.eventType === 'DEPARTURE')),
  arrItems: (state, getters) =>
    Object.values(getters.allItems.filter((item) => item.eventType === 'ARRIVAL')),
  starredItems: (state, getters) =>
    Object.values(
      getters.allItems.filter((item) =>
        state.filter.starredEvents.some(
          (starred) =>
            starred.flight.number === item.flight.number &&
            starred.eventType === item.eventType
        )
      )
    ),
  standItems: (state, getters) =>
    Object.values(
      getters.allItems.filter((item) =>
        item.eventType === 'ARRIVAL'
          ? state.filter.stands.includes(item.flight.arrivalStand)
          : state.filter.stands.includes(item.flight.departureStand)
      )
    ),
  allStands: (state) =>
    Object.values(state.items)
      .map((item) =>
        item.eventType === 'ARRIVAL'
          ? item.flight.arrivalStand
          : item.flight.departureStand
      )
      .filter(Boolean)
      .sort()
}

const actions = {
  initStore({ commit }) {
    const rawFilter = sessionStorage.getItem('filter')
    if (rawFilter && rawFilter !== 'null') {
      commit('setFilter', JSON.parse(rawFilter))
    }
  },
  updateAndApplyFilter({ commit, dispatch }, filter) {
    commit('setFilter', filter)
    dispatch('requestStream')
  },
  updateFilter({ commit }, filter) {
    commit('setFilter', filter)
  },
  toggleStarEvent({ commit, state }, event) {
    const filtered = state.filter.starredEvents.filter(
      (e) => eventId(event) !== eventId(e)
    )
    if (state.filter.starredEvents.length === filtered.length) {
      commit('setFilter', {
        ...state.filter,
        starredEvents: [...state.filter.starredEvents, event]
      })
    } else {
      commit('setFilter', { ...state.filter, starredEvents: filtered })
    }
  },
  saveFilterAsDefault({ state, dispatch }) {
    dispatch(
      'updateUserPreference',
      { defaultMonitoringFilter: state.filter },
      { root: true }
    )
  },
  async requestStream({ commit, state }) {
    const user = await store.dispatch('oidcStore/getOidcUser')
    const token = user.id_token
    try {
      commit('cancelSubscription')
      commit('isSocketAlive', true)
      let socket = await rsocket.socket()

      const metadata = encodeAndAddWellKnownMetadata(
        encodeAndAddCustomMetadata(
          Buffer.alloc(0),
          'message/x.rsocket.authentication.v0',
          encodeBearer(token)
        ),
        MESSAGE_RSOCKET_ROUTING,
        encodeRoute('stream')
      )
      socket
        .requestStream({
          data: Buffer.from(JSON.stringify(state.request), 'utf8'),
          metadata
        })
        .subscribe({
          onError: (error) => {
            console.log(error)
            commit('isFeedAlive', false)
            commit('isSocketAlive', false)
          },
          onNext: (value) => {
            const itemEvent = JSON.parse(value.data)
            if (itemEvent.changeType === 'UPDATE') {
              commit('updateItem', {
                id: eventId(itemEvent),
                item: {
                  eventType: itemEvent.eventType,
                  flight: itemEvent.flight,
                  time:
                    itemEvent.eventType === 'ARRIVAL'
                      ? itemEvent.flight.timeArrival
                      : itemEvent.flight.timeDeparture
                }
              })
            } else {
              commit('removeItem', { id: eventId(itemEvent) })
            }
          },
          // Nothing happens until `request(n)` is called
          onSubscribe: (sub) => {
            commit('setItems', { items: {} })
            commit('setSubscription', sub)
            commit('isFeedAlive', true)
            sub.request(MAX_STREAM_ID)
          }
        })
    } catch (e) {
      console.error(e)
      commit('isSocketAlive', false)
    }
  }
}

// mutations
const mutations = {
  setSubscription(state, val) {
    state.subscription = val
  },
  cancelSubscription(state) {
    if (state.subscription) {
      state.subscription.cancel()
      state.subscription = null
    }
  },
  isFeedAlive(state, val) {
    state.isFeedAlive = val
  },
  isSocketAlive(state, val) {
    state.isSocketAlive = val
  },
  setFilter(state, filter) {
    state.filter = filter
    sessionStorage.setItem('filter', JSON.stringify(state.filter))
  },
  setItems(state, { items }) {
    state.items = items
  },
  updateItem(state, { id, item }) {
    console.log('update ' + id)
    Vue.set(state.items, id, item)
  },
  removeItem(state, { id }) {
    console.log('delete ' + id)
    Vue.delete(state.items, id)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
